/**
 * Cockpit ambient traffic — pure-visual NPC cars scrolling on the road.
 *
 * Lives entirely client-side in cockpit view to give the "you're driving in
 * the city dodging cars" feeling per the user's reference photos. NOT
 * collidable — driving "through" them is fine. They only render in
 * cockpit projection and read the player's position to scroll relative.
 *
 * State is stored on `st.cockpitTraffic = [{x, y, color, lane}, ...]`.
 */

const MAX_CARS = 5;
// Spawn distance ahead of the player. Tightened so traffic is always
// visibly mid-distance — too far means tiny specks at the vanishing point.
const SPAWN_NEAR = 150;
const SPAWN_FAR = 500;
// How far behind the player before a car is recycled.
const DESPAWN_BEHIND = 250;
// 4 colorways for visual variety.
const PALETTES = [
  { body: "#E63946", roof: "#FFFFFF" },  // red sedan
  { body: "#F1C40F", roof: "#1A1A1A" },  // yellow taxi
  { body: "#3498DB", roof: "#1A1A1A" },  // blue compact
  { body: "#FFFFFF", roof: "#1A1A1A" },  // white sedan
  { body: "#34495E", roof: "#FFFFFF" },  // dark sedan
];

/** Initialise traffic on demand. Idempotent. */
export function ensureCockpitTraffic(st) {
  if (!st.cockpitTraffic) st.cockpitTraffic = [];
}

/**
 * Per-frame update — spawn / despawn / scroll. Does NOT move the cars
 * relative to the world; they have absolute positions in world space and
 * the projection takes care of the rest. They DO drift slightly downstream
 * (vy) so they appear to drive on the road, sometimes overtaking each
 * other.
 */
export function tickCockpitTraffic(st, _car) {
  ensureCockpitTraffic(st);
  const list = st.cockpitTraffic;
  const p = st.player;
  const fwdX = Math.cos(p.angle || 0);
  const fwdY = Math.sin(p.angle || 0);

  // Despawn cars that fall too far behind in the player's forward axis.
  // We project (car.pos - player.pos) onto the player's forward vector
  // — when that scalar is too negative, the car is well behind us; when
  // it's much greater than SPAWN_FAR, the player has somehow teleported
  // past them so recycle.
  for (let i = list.length - 1; i >= 0; i--) {
    const dx = list[i].x - p.x;
    const dy = list[i].y - p.y;
    const along = dx * fwdX + dy * fwdY;
    if (along < -DESPAWN_BEHIND || along > SPAWN_FAR * 1.6) {
      list.splice(i, 1);
    }
  }

  // Spawn until full. Cars spawn ahead of the player in the direction
  // the player is currently facing. NPCs are placed at random lateral
  // lanes off the player's heading. Without the angle math, NPCs would
  // spawn at +y absolute world coords — but the player's "forward" depends
  // on `player.angle`, so we project the spawn distance through that
  // heading vector. Lanes are tight (±60px) so cars stay on the road
  // surface, not the grass shoulders.
  while (list.length < MAX_CARS) {
    const palette = PALETTES[Math.floor(Math.random() * PALETTES.length)];
    const lane = [-50, -20, 20, 50][Math.floor(Math.random() * 4)];
    const dist = SPAWN_NEAR + Math.random() * (SPAWN_FAR - SPAWN_NEAR);
    const fwdX = Math.cos(p.angle || 0);
    const fwdY = Math.sin(p.angle || 0);
    const rightX = -fwdY;
    const rightY = fwdX;
    list.push({
      x: p.x + fwdX * dist + rightX * lane,
      y: p.y + fwdY * dist + rightY * lane,
      vy: 1.2 + Math.random() * 1.4,
      lane,
      ...palette,
    });
  }

  // Scroll forward in the player's heading (slower than player so the
  // player overtakes them). The player will literally drive past these
  // cars, which is the desired "in traffic" feel.
  for (const t of list) {
    t.x += fwdX * t.vy;
    t.y += fwdY * t.vy;
    // Subtle lateral drift so cars don't feel rigid.
    const rightX = -fwdY;
    const rightY = fwdX;
    const wobble = Math.sin(t.y * 0.01) * 0.15;
    t.x += rightX * wobble;
    t.y += rightY * wobble;
  }
}

/** Returns the list for the cockpit projection loop to consume. */
export function getCockpitTraffic(st) {
  return st.cockpitTraffic || [];
}

/**
 * Render a single ambient car — called from the cockpit projection loop.
 * Drawn as a chunky pixel-art top-down sedan: body + windshield + roof +
 * 4 wheels + taillights. Scales with depth.
 */
export function drawTrafficCar(ctx, proj, data) {
  const { px, py, scale } = proj;
  const w = Math.max(14, 56 * scale);
  const h = Math.max(9, 32 * scale);
  ctx.save();

  // shadow
  ctx.fillStyle = "rgba(0,0,0,0.4)";
  ctx.beginPath();
  ctx.ellipse(px, py + h / 2 + 2, w / 2 + 4, 5, 0, 0, Math.PI * 2);
  ctx.fill();

  // wheels — drawn FIRST so the body overlaps them slightly
  const wheelW = Math.max(3, 8 * scale);
  const wheelH = Math.max(2, 5 * scale);
  ctx.fillStyle = "#0E0C12";
  ctx.fillRect(px - w / 2 - 1, py - h / 2 - 1, wheelW, wheelH);              // front-left
  ctx.fillRect(px + w / 2 - wheelW + 1, py - h / 2 - 1, wheelW, wheelH);     // front-right
  ctx.fillRect(px - w / 2 - 1, py + h / 2 - wheelH + 1, wheelW, wheelH);     // rear-left
  ctx.fillRect(px + w / 2 - wheelW + 1, py + h / 2 - wheelH + 1, wheelW, wheelH);  // rear-right

  // body
  ctx.fillStyle = data.body || "#E63946";
  ctx.fillRect(px - w / 2, py - h / 2, w, h);
  // body outline
  ctx.strokeStyle = "#0E0C12";
  ctx.lineWidth = Math.max(1, 1.5 * scale);
  ctx.strokeRect(px - w / 2, py - h / 2, w, h);

  // roof / cabin (inset)
  const roofW = w * 0.62;
  const roofH = h * 0.55;
  ctx.fillStyle = data.roof || "#1A1A1A";
  ctx.fillRect(px - roofW / 2, py - roofH / 2, roofW, roofH);

  // windshield band (front of cabin) — light blue
  ctx.fillStyle = "rgba(135,180,220,0.65)";
  ctx.fillRect(px - roofW / 2, py - roofH / 2, roofW, Math.max(2, roofH * 0.22));

  // taillights — red, top edge of body (rear since car faces away from player)
  ctx.fillStyle = "#FF3030";
  const taillightW = Math.max(2, 5 * scale);
  const taillightH = Math.max(2, 3 * scale);
  ctx.fillRect(px - w / 2 + 2, py - h / 2 + 2, taillightW, taillightH);
  ctx.fillRect(px + w / 2 - 2 - taillightW, py - h / 2 + 2, taillightW, taillightH);

  ctx.restore();
}
